import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
    Flex,
    Box,
    Button,
    Checkbox,
    CheckButton,
    Copy,
    Image,
    WebviewWrapper,
} from '@elparking/components'

import PlaceSelectWrapper from './components/PlaceSelectWrapper'
import SyncBookingDates from 'commons/js/components/SyncBookingDates'
import {
    toDate,
    makeHourTimeSpan,
    makeMinuteTimeSpan,
    maybeParseTimestamp,
    toTimestamp,
} from 'commons/js/util/dateConverter'
import DateTimePicker from 'commons/js/ui/DateTimePicker'
import messages from './messages'
import {
    LOCALE_DATE_FORMAT,
    LOCALE_DATETIME_FORMAT,
    RENTAL_CAR_VEHICLE_TYPE_CAR,
    RENTAL_CAR_MINUTES_STEP,
    RENTAL_CAR_HOURS_STEP,
    DEBOUNCED_INPUT_TIME,
} from 'commons/js/constants'
import AgeSelector from './components/AgeSelector'
import VehicleTypeFilter from 'commons/js/components/VehicleTypeFilter'
import { sendCloseWebviewEvent } from 'commons/js/services/sendMessage'
import {
    RentalCarEvents,
    trackTealiumEvent,
} from 'commons/js/services/analytics'
import SuccessMessage from 'rental-car-contract/js/components/RentalCarContract/SuccessMessage'
import UrlParams from 'commons/js/util/UrlTo/UrlParams'
import { calculateNextFraction } from 'commons/js/services/dateCalculator'
import { debounceAsyncFn } from 'commons/js/util/promiseUtils'

const IsMutuaUserCheckComponent = ({ intl, isMutua, setIsMutua }) => (
    <Box ml={['0em', '0em', '0em', '1em']} minHeight={['0', '45px']}>
        <Checkbox
          checkButtonElement={CheckButton.iconFilled}
          squared
          id='check-is-mutua'
          checked={isMutua}
          dataTest={'check-is-mutua'}
          justifyContent='start'
          fontSize='xSmall'
          fontColor='gray'
          fontWeight='book'
          type='checkbox'
          lineHeight='20px'
          label={intl.formatMessage(messages.isMutuaCheckLabel)}
          onChange={() => setIsMutua(!isMutua)}
        />
    </Box>
)

IsMutuaUserCheckComponent.propTypes = {
    isMutua: PropTypes.bool,
    setIsMutua: PropTypes.func,
    intl: PropTypes.object,
}

const getDefaultDates = () => {
    const twoHoursInMilliseconds = 2 * 60 * 60 * 1000
    const defaultStartDate = Date.now() + twoHoursInMilliseconds
    const sevenDaysInMilliseconds = 7 * 24 * 60 * 60 * 1000

    return {
        defaultStartDate: toTimestamp(
            calculateNextFraction(defaultStartDate, RENTAL_CAR_MINUTES_STEP)
        ),
        defaultEndDate: toTimestamp(
            calculateNextFraction(
                defaultStartDate + sevenDaysInMilliseconds,
                RENTAL_CAR_MINUTES_STEP
            )
        ),
    }
}

const RentalCarForm = ({ intl, autocompleteService, onSelect, search }) => {
    const [vehicleType, setVehicleType] = useState(
        (search && search.vehicleType) || RENTAL_CAR_VEHICLE_TYPE_CAR
    )
    const [pickUpPlace, setPickUpPlace] = useState(
        (search && search.pickUpPlace) || null
    )
    const [pickUpInputSearch, setPickUpInputSearch] = useState(
        (search && search.pickUpInputSearch) || null
    )
    const [returnPlace, setReturnPlace] = useState(
        (search && search.returnUpPlace) || null
    )
    const [returnPlaceInputSearch, setReturnPlaceInputSearch] = useState(
        (search && search.returnPlaceInputSearch) || null
    )
    const [isDifferentReturnLocation, setIsDifferentReturnLocation] = useState(
        (search && search.isDifferentReturnLocation) || false
    )
    const [isMutua, setIsMutua] = useState(
        (search && search.isMutuaUser) || true
    )
    const [isSubmit, setIsSubmit] = useState(false)
    const { defaultStartDate, defaultEndDate } = getDefaultDates()
    const [dates, setDates] = useState({
        startDate: (search && search.startDate) || defaultStartDate,
        endDate: (search && search.endDate) || defaultEndDate,
    })
    const [age, setAge] = useState((search && search.age) || '28')
    const [isStartDateOpen, setIsStartDateOpen] = useState(false)
    const [isEndDateOpen, setIsEndDateOpen] = useState(false)
    const hasPickUpPlace = pickUpPlace && pickUpPlace.value
    const hasReturnPlace = returnPlace && returnPlace.value
    const autocompleteServiceDebounced = debounceAsyncFn(
        autocompleteService,
        DEBOUNCED_INPUT_TIME
    )
    const onSubmit = (event) => {
        event && event.preventDefault()
        setIsSubmit(true)
        if (
            hasPickUpPlace &&
            hasReturnPlace &&
            dates.startDate &&
            dates.endDate &&
            age
        ) {
            trackTealiumEvent(RentalCarEvents.clickSearchLink)
            onSelect({
                share: new UrlParams().loadFromUrl().get('share'),
                pickUpPlace,
                pickUpInputSearch,
                returnPlace,
                returnPlaceInputSearch,
                dates,
                isMutua,
                isDifferentReturnLocation,
                age,
                vehicleType,
            })
        }
    }

    const onHandlePlace = (place, input) => {
        setPickUpPlace(place)
        setPickUpInputSearch(input)
        if (!isDifferentReturnLocation) {
            setReturnPlace(place)
            setReturnPlaceInputSearch(input)
        }
    }

    const onHandleIsDifferentReturnLocation = (isChecked) => {
        setIsDifferentReturnLocation(isChecked)
        isChecked ? setReturnPlace(null) : setReturnPlace(pickUpPlace)
    }
    const handleChangeAgeSelector = ({ value }) => {
        trackTealiumEvent(RentalCarEvents.ageSelectorCarBooking(value))
        setAge(value)
    }
    useEffect(() => {
        trackTealiumEvent(RentalCarEvents.showLandingView)
    }, [])

    const openStartDatePicker = () => {
        setIsStartDateOpen(true)
        setIsEndDateOpen(false)
    }
    const openEndDatePicker = () => {
        setIsEndDateOpen(true)
        setIsStartDateOpen(false)
    }
    const renderPlacesSelect = () => {
        return (
            <Flex
              width='100%'
              flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}
              maxWidth={[
                    'calc(100vw - 75px)',
                    'calc(100vw - 75px)',
                    'calc(100vw - 140px)',
                    '490px',
                ]}
            >
                <Box
                  width='100%'
                  maxWidth={
                        isDifferentReturnLocation
                            ? ['100%', '100%', '50%']
                            : ['100%', '100%', '100%']
                    }
                >
                    <PlaceSelectWrapper
                      dataTest='rental-car-search-form-places-pickUp'
                      value={pickUpPlace}
                      hideClearButton
                      initialSearchValue={
                            search?.pickUpPlace && {
                                ...search.pickUpPlace,
                                inputSearch: search.pickUpInputSearch,
                            }
                        }
                      autocompleteService={autocompleteServiceDebounced}
                      placeholder={intl.formatMessage(
                            messages.placePlaceholder
                        )}
                      label={intl.formatMessage(
                            isDifferentReturnLocation
                                ? messages.pickUpLabelWithSameLocation
                                : messages.pickUpLabel
                        )}
                      onChange={onHandlePlace}
                      error={isSubmit && !hasPickUpPlace}
                      errorMessage={intl.formatMessage(
                            messages.pickUpPlaceError
                        )}
                      withLiteralInput
                      ariaRoleDescription={intl.formatMessage(messages.ariaPickUpPlaceInputDescription)}
                    />
                </Box>
                <Box
                  display={['block', 'block', 'block', 'none']}
                  width='100%'
                  mt='0.5em'
                >
                    <Checkbox
                      checkButtonElement={CheckButton.iconFilled}
                      squared
                      id='check-different-return-location'
                      checked={isDifferentReturnLocation}
                      dataTest='check-different-return-location'
                      justifyContent='start'
                      fontSize='small'
                      fontColor='gray'
                      fontWeight='book'
                      type='checkbox'
                      label={intl.formatMessage(
                            messages.isDifferentOfficeLabel
                        )}
                      onChange={() =>
                            onHandleIsDifferentReturnLocation(
                                !isDifferentReturnLocation
                            )
                        }
                    />
                </Box>
                {isDifferentReturnLocation && (
                    <Box
                      width='100%'
                      ml={['0em', '0em', '0em', '1em']}
                      maxWidth={['100%', '100%', 'calc(50% - 1em)']}
                    >
                        <PlaceSelectWrapper
                          dataTest='rental-car-search-form-places-return'
                          icon='len'
                          showGoogleBanner
                          initialSearchValue={
                                search?.returnUpPlace && {
                                    ...search.returnUpPlace,
                                    inputSearch: search.returnPlaceInputSearch,
                                }
                            }
                          value={returnPlace}
                          autocompleteService={autocompleteServiceDebounced}
                          placeholder={intl.formatMessage(
                                messages.placePlaceholder
                            )}
                          label={intl.formatMessage(messages.returnLabel)}
                          onChange={(place, input) => {
                                setReturnPlace(place)
                                setReturnPlaceInputSearch(input)
                            }}
                          error={isSubmit && !hasReturnPlace}
                          errorMessage={intl.formatMessage(
                                messages.returnPlaceError
                          )}
                          ariaRoleDescription={intl.formatMessage(messages.ariaReturnPlaceInputDescription)}
                        />
                    </Box>
                )}
            </Flex>
        )
    }
    return (
        <>
            <Flex
              as='form'
              backgroundColor='white'
              padding='20px'
              zIndex={9}
              flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}
              flexDirection='column'
              alignItems='stretch'
              minHeight='280px'
            >
                <Box flex={['1 0 100%', '1 0 100%', '1 0 100%', '1 0 auto']}>
                    <WebviewWrapper>
                        <Box textAlign='left'>
                            <Button.unstyled
                              type='button'
                              onClick={() => sendCloseWebviewEvent()}
                            >
                                <Image
                                  alt={'back-button'}
                                  src='/images/rental-car/back-button.svg'
                                />
                            </Button.unstyled>
                        </Box>
                        <Box mt='1em' mb='1em'>
                            {search.isCancelled && (
                                <SuccessMessage
                                  message={
                                      <FormattedMessage defaultMessage='Tu reserva ha sido cancelada' />
                                    }
                                />
                            )}
                            <Copy fontSize='xxLarge' fontWeight='medium'>
                                <FormattedMessage defaultMessage='Alquiler de coches' />
                            </Copy>
                            <Copy fontSize='small' fontWeight='regular'>
                                <FormattedMessage defaultMessage='Elige el tuyo entre una amplia flota a un precio exclusivo, con cancelación gratuita y cobertura total sin franquicia' />
                            </Copy>
                        </Box>
                    </WebviewWrapper>
                    <Box marginBottom='1em'>
                        <VehicleTypeFilter
                          vehicleType={vehicleType}
                          setVehicleType={setVehicleType}
                        />
                    </Box>
                    <Flex flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}>
                        <Box width='100%'>{renderPlacesSelect()}</Box>
                        <Box width='100%' ml={['0em', '0em', '0em', '0.5em']}>
                            <SyncBookingDates
                              startDate={dates.startDate}
                              endDate={dates.endDate}
                              timeSpan={makeHourTimeSpan(
                                    RENTAL_CAR_HOURS_STEP
                                )}
                              minTimeSpan={makeMinuteTimeSpan(
                                    RENTAL_CAR_MINUTES_STEP
                                )}
                              onChange={setDates}
                            >
                                {({
                                    startDateProps,
                                    endDateProps,
                                    onChangeStart,
                                    onChangeEnd,
                                }) => (
                                    <Flex
                                      flexWrap={[
                                            'wrap',
                                            'wrap',
                                            'wrap',
                                            'nowrap',
                                        ]}
                                    >
                                        <Box width='100%'>
                                            <DateTimePicker
                                              value={
                                                    startDateProps.value &&
                                                    toDate(
                                                        maybeParseTimestamp(
                                                            startDateProps.value
                                                        )
                                                    )
                                                }
                                              format={LOCALE_DATE_FORMAT}
                                              mobileDateFormat={
                                                    LOCALE_DATETIME_FORMAT
                                                }
                                              dayPickerProps={{
                                                    month: startDateProps.month,
                                                    fromMonth:
                                                        startDateProps.fromMonth,
                                                    disabledDays:
                                                        startDateProps.disabledDays,
                                                    locale: intl.locale,
                                                }}
                                              timePickerProps={{
                                                    disabledHours:
                                                        startDateProps.disabledHours,
                                                }}
                                              dataTest='rental-car-search-form-init-date'
                                              onChange={onChangeStart}
                                              label={intl.formatMessage(
                                                    messages.pickUpDateLabel
                                                )}
                                              datePlaceholder={intl.formatMessage(
                                                    messages.datePlaceholder
                                                )}
                                              hourPlaceholder={intl.formatMessage(
                                                    messages.hourPlaceholder
                                                )}
                                              mobileDatePlaceholder={intl.formatMessage(
                                                    messages.mobileDatePlaceholder
                                                )}
                                              error={
                                                    isSubmit && !dates.startDate
                                                }
                                              errorMessage={intl.formatMessage(
                                                    messages.pickUpTimeError
                                                )}
                                              minutesStep={
                                                    RENTAL_CAR_MINUTES_STEP
                                                }
                                              onNext={() => {
                                                    setIsEndDateOpen(true)
                                                    setIsStartDateOpen(false)
                                                }}
                                              onFocus={() => {
                                                    isStartDateOpen ? setIsStartDateOpen(false) : openStartDatePicker()
                                              }}
                                              onBlur={() => {
                                                    setIsStartDateOpen(false)
                                                }}
                                              isOpen={isStartDateOpen}
                                            />
                                            {isSubmit && !dates.startDate && (
                                                <Copy
                                                  padding='0'
                                                  mt='4px'
                                                  fontColor='error'
                                                  fontWeight='regular'
                                                >
                                                    <FormattedMessage defaultMessage='Elige la fecha de recogida' />
                                                </Copy>
                                            )}
                                        </Box>
                                        <Box
                                          width='100%'
                                          ml={['0em', '0em', '0em', '0.5em']}
                                        >
                                            <DateTimePicker
                                              value={
                                                    endDateProps.value &&
                                                    toDate(
                                                        maybeParseTimestamp(
                                                            endDateProps.value
                                                        )
                                                    )
                                                }
                                              format={LOCALE_DATE_FORMAT}
                                              mobileDateFormat={
                                                    LOCALE_DATETIME_FORMAT
                                                }
                                              dayPickerProps={{
                                                    month: endDateProps.month,
                                                    fromMonth:
                                                        endDateProps.fromMonth,
                                                    disabledDays:
                                                        endDateProps.disabledDays,
                                                    locale: intl.locale,
                                                }}
                                              dataTest='rental-car-search-form-end-date'
                                              timePickerProps={{
                                                    disabledHours:
                                                        endDateProps.disabledHours,
                                                }}
                                              onChange={onChangeEnd}
                                              label={intl.formatMessage(
                                                    messages.returnDateLabel
                                                )}
                                              datePlaceholder={intl.formatMessage(
                                                    messages.datePlaceholder
                                                )}
                                              hourPlaceholder={intl.formatMessage(
                                                    messages.hourPlaceholder
                                                )}
                                              mobileDatePlaceholder={intl.formatMessage(
                                                    messages.mobileDatePlaceholder
                                                )}
                                              error={
                                                    isSubmit && !dates.endDate
                                                }
                                              minutesStep={
                                                    RENTAL_CAR_MINUTES_STEP
                                                }
                                              onNext={() => {
                                                    setIsEndDateOpen(false)
                                                    setIsStartDateOpen(true)
                                                }}
                                              onFocus={() => {
                                                  isEndDateOpen ? setIsEndDateOpen(false) : openEndDatePicker()
                                              }}
                                              onBlur={() => {
                                                    setIsEndDateOpen(false)
                                                }}
                                              isOpen={isEndDateOpen}
                                            />
                                            {isSubmit && !dates.endDate && (
                                                <Copy
                                                  padding='0'
                                                  mt='4px'
                                                  fontColor='error'
                                                  fontWeight='regular'
                                                >
                                                    <FormattedMessage defaultMessage='Elige la fecha de devolución' />
                                                </Copy>
                                            )}
                                        </Box>
                                    </Flex>
                                )}
                            </SyncBookingDates>
                        </Box>
                    </Flex>
                </Box>
                <Flex
                  alignItems={'flex-start'}
                  justifyContent='flex-start'
                  flexDirection={['column', 'column', 'column', 'row']}
                  mt='1em'
                >
                    <Box
                      display={['none', 'none', 'none', 'block']}
                      minHeight={['0', '45px']}
                    >
                        <Checkbox
                          checkButtonElement={CheckButton.iconFilled}
                          squared
                          id='check-different-return-location'
                          checked={isDifferentReturnLocation}
                          dataTest='check-different-return-location'
                          justifyContent='start'
                          fontSize='xSmall'
                          fontColor='gray'
                          fontWeight='book'
                          lineHeight='20px'
                          type='checkbox'
                          label={intl.formatMessage(
                                messages.isDifferentOfficeLabel
                            )}
                          onChange={() =>
                                onHandleIsDifferentReturnLocation(
                                    !isDifferentReturnLocation
                                )
                            }
                        />
                    </Box>
                    <WebviewWrapper
                      fallbackComponent={
                          <IsMutuaUserCheckComponent
                            isMutua={isMutua}
                            setIsMutua={setIsMutua}
                            intl={intl}
                            />
                        }
                    >
                        <Box />
                    </WebviewWrapper>
                    <Box
                      ml={['0em', '0em', '0em', '1em']}
                      minHeight={['0', '45px']}
                    >
                        <AgeSelector
                          value={age}
                          onChange={handleChangeAgeSelector}
                          error={isSubmit && !age}
                        />
                    </Box>
                </Flex>
                <Flex
                  width='100%'
                  alignItems='center'
                  justifyContent='space-between'
                  flexDirection={[
                        'column-reverse',
                        'column-reverse',
                        'column-reverse',
                        'row',
                    ]}
                >
                    <Flex
                      flexWrap='wrap'
                      alignItems='center'
                      justifyContent={['center', 'flex-start']}
                      width='100%'
                      mt='0.5em'
                    >
                        <Image
                          mt={['0.5em', '0.5em', '0.5em', '0em']}
                          mr='1em'
                          located={false}
                          src='/images/rental-car/centauro-gray-contrast.svg'
                          height='23px'
                          alt='App'
                        />
                        <Image
                          mt={['0.5em', '0.5em', '0.5em', '0em']}
                          mr='1em'
                          located={false}
                          src='/images/rental-car/europcar-gray-contrast.svg'
                          alt='App'
                          height='26px'
                        />
                        <Image
                          mt={['0.5em', '0.5em', '0.5em', '0em']}
                          located={false}
                          src='/images/rental-car/sixt-gray-contrast.svg'
                          alt='App'
                          height='20px'
                        />
                    </Flex>
                    <Box width='100%' textAlign='right' mt='0.5em'>
                        <Button
                          dataTest='rental-car-search-form-search-button'
                          size='xLarge'
                          fontWeight='medium'
                          color='white'
                          width={['100%', '100%', '100%', '180px']}
                          onClick={onSubmit}
                          backgroundColor='darkMain60'
                        >
                            <FormattedMessage defaultMessage='Buscar' />
                        </Button>
                    </Box>
                </Flex>
            </Flex>
        </>
    )
}

RentalCarForm.propTypes = {
    intl: PropTypes.object,
    autocompleteService: PropTypes.func,
    onSelect: PropTypes.func,
    search: PropTypes.object,
}

RentalCarForm.defaultProps = {
    onSelect: () => {},
    search: {
        isCancelled: false,
    },
}

export default injectIntl(RentalCarForm)
